// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A filter menu component for asset location selection.
 * This module provides a hierarchical interface for filtering
 * assets based on their location, with support for grouping
 * by customer/manufacturer and multi-level selection. It
 * handles complex selection states and group operations.
 *
 * Key features:
 * - Hierarchical selection with zone sub-layers
 * - Customer/manufacturer grouping
 * - Multi-level filtering
 * - Search filtering
 * - Real-time updates
 */
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import {
  FilterMenuRow,
  VirtualizedNestedFilterMenu,
} from '@/components/virtualized-nested-filter-menu';
import { LocationCompanyType } from '@/constants/location-company-type';
import { useStableValue } from '@/hooks/use-stable-value';
import { type LocationFilter } from '@/location/api/get-locations';
import { useLocations } from '@/location/hooks/use-locations';
import { getEffectiveLocationName } from '@/location/utilities/get-effective-third-party';
import { isSameStringList } from '@/utilities/is-same-string-list';
import { pluralize } from '@/utilities/pluralize';
import {
  compact,
  groupBy,
  keyBy,
  partition,
  sortBy,
  sumBy,
  uniq,
  without,
} from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef } from 'react';

const DEFAULT_LABEL = 'Location';

// The "Default" zone is the site's own ID — assets at the site but in no zone
const DEFAULT_ZONE_LABEL = 'Default';

const INVENTORY_CHIP_LABEL = 'Inventory';
const MIN_MENU_WIDTH = 360;
const NO_LOCATIONS_MESSAGE = 'No locations found';
const NO_MATCHES_MESSAGE = 'No matching locations';

interface ExtraOption {
  id: string;
  label: string;
  isActive: boolean;
  onChange: (isActive: boolean) => void;
}

/**
 * Props for configuring the AssetLocationFilterMenu component.
 *
 * @interface Props
 */
interface Props {
  /**
   * Types of companies to include in the filter.
   * Controls high-level grouping of locations.
   */
  companyTypes: LocationCompanyType[];

  /**
   * IDs of customers whose locations are selected.
   * Used for group-level selection state.
   */
  customerIds: string[];

  /**
   * Individual location IDs that are selected.
   * Empty array means no locations are filtered.
   */
  locationIds: string[];

  /**
   * The label for the filter menu button.
   * Displayed when menu is closed.
   *
   * @default "Location"
   */
  label?: string;

  /**
   * Optional location query filter. Asset list callers use the default active
   * location set, while historical reports can include archived/transient
   * locations without changing the menu's selection behavior.
   */
  locationFilter?: LocationFilter;

  /**
   * Synthetic choices that should appear in the same menu as locations, but
   * are not real location records.
   */
  extraOptions?: ExtraOption[];

  /**
   * Callback fired when selection changes.
   * Receives updated arrays for all selection types.
   *
   * @param locationIds - New array of selected location IDs
   * @param customerIds - New array of selected customer IDs
   * @param companyTypes - New array of selected company types
   */
  onChange: (
    locationIds: string[],
    customerIds: string[],
    companyTypes: LocationCompanyType[]
  ) => void;
}

/**
 * Whether a site needs its own "Default" row — that is, whether it has zones
 * to distinguish the default from.
 *
 * Keyed on the returned zones, which is what getSiteAllIds selects on: a site
 * whose zones are all archived selects only its own ID, so it renders as a
 * single "Site — Default" row rather than a site row plus an identical child.
 */
function hasDefaultZone(site: { zones: { id: string }[] }): boolean {
  return site.zones.length > 0;
}

/**
 * Returns all IDs representing full selection of a site:
 * the site's own ID plus all zone IDs.
 */
function getSiteAllIds(site: {
  id: string;
  zones: { id: string }[];
}): string[] {
  return [site.id, ...site.zones.map((z) => z.id)];
}

/**
 * Computes active/indeterminate state for a site row. A site is active when
 * its group is active or all of its IDs are selected, and indeterminate when
 * only some are.
 */
function getSiteSelection(
  option: { id: string; zones: { id: string }[] },
  selectedLocationIds: ReadonlySet<string>,
  isGroupActive: boolean
): { isActive: boolean; isIndeterminate: boolean } {
  const siteAllIds = getSiteAllIds(option);
  const selectedCount = siteAllIds.filter((id) =>
    selectedLocationIds.has(id)
  ).length;
  const isActive = isGroupActive || selectedCount === siteAllIds.length;
  const isIndeterminate = !isActive && selectedCount > 0;
  return { isActive, isIndeterminate };
}

function isSameExtraOptions(
  previous: ExtraOption[],
  next: ExtraOption[]
): boolean {
  return (
    previous.length === next.length &&
    previous.every(
      (option, index) =>
        option.id === next[index].id &&
        option.label === next[index].label &&
        option.isActive === next[index].isActive
    )
  );
}

/**
 * A hierarchical filter menu for location selection.
 * Provides a grouped interface for selecting locations
 * with support for customer/manufacturer organization,
 * zone sub-layers, search, and multi-level selection operations.
 * Uses virtualization for performance with large location lists.
 */
export function AssetLocationFilterMenu(props: Props) {
  const { user } = useCurrentUser();
  const { locations } = useLocations({ filter: props.locationFilter });

  const label = props.label ?? DEFAULT_LABEL;

  // Build sorted options with labels
  const allOptions = useMemo(
    () =>
      sortBy(locations, (location) => location.name).map((location) => ({
        ...location,
        // Sites with zones list "Default" as their own row, so the site row is
        // just the site name
        label: hasDefaultZone(location)
          ? location.name
          : getEffectiveLocationName(location),
        zones: sortBy(location.zones, (zone) => zone.name),
      })),
    [locations]
  );

  const customers = useMemo(
    () => keyBy(compact(locations.map(({ customer }) => customer)), 'id'),
    [locations]
  );

  const [allManufacturerOptions, allCustomerOptions] = useMemo(
    () => partition(allOptions, (option) => option.customer === null),
    [allOptions]
  );

  const allCustomerGroups = useMemo(
    () => groupBy(allCustomerOptions, (option) => option.customer?.id),
    [allCustomerOptions]
  );

  const isManufacturerGroupActive = props.companyTypes.includes('manufacturer');
  const isCustomerGroupActive = props.companyTypes.includes('customer');

  // Callers pass these as fresh literals, so each is held at its last content —
  // which is what lets the row builder below stay memoized across a render
  // that changed nothing it reads
  const selectedLocations = useStableValue(props.locationIds, isSameStringList);
  const selectedCustomers = useStableValue(props.customerIds, isSameStringList);
  const extraOptions = useStableValue(
    props.extraOptions ?? [],
    isSameExtraOptions
  );

  // Sets, not the arrays: membership is asked once per site and once per zone
  // on every render, which the array form turns into a scan apiece
  const selectedLocationIds = useMemo(
    () => new Set(selectedLocations),
    [selectedLocations]
  );
  const selectedCustomerIds = useMemo(
    () => new Set(selectedCustomers),
    [selectedCustomers]
  );

  // Selections whose location or customer is absent from the fetched set — an
  // archived site, say. They have no row of their own to uncheck, so without
  // this they filter the list invisibly and cannot be cleared from the menu.
  const orphanSelection = useMemo(() => {
    const knownLocationIds = new Set(allOptions.flatMap(getSiteAllIds));

    return {
      locationIds: selectedLocations.filter((id) => !knownLocationIds.has(id)),
      customerIds: selectedCustomers.filter((id) => !(id in allCustomerGroups)),
    };
  }, [allOptions, allCustomerGroups, selectedLocations, selectedCustomers]);

  const orphanCount =
    orphanSelection.locationIds.length + orphanSelection.customerIds.length;

  // Both of these walk every location and build a fresh id array per site, so
  // they are memoized rather than recomputed whenever the page around the menu
  // renders. The group flags are read as booleans, which keeps companyTypes —
  // a fresh array from every caller — out of the dependencies.
  const isCustomerGroupIndeterminate = useMemo(
    () =>
      !isCustomerGroupActive &&
      (Object.keys(customers).some((id) => selectedCustomerIds.has(id)) ||
        allCustomerOptions.some((option) =>
          getSiteAllIds(option).some((id) => selectedLocationIds.has(id))
        )),
    [
      allCustomerOptions,
      customers,
      isCustomerGroupActive,
      selectedCustomerIds,
      selectedLocationIds,
    ]
  );

  // Count active sites using the full (unfiltered) option list.
  // A site counts as active if any of its IDs (site + zones) are selected
  // or a parent group is active.
  const count = useMemo(
    () =>
      sumBy(allOptions, (option) => {
        const allIds = getSiteAllIds(option);

        if (option.customer === null) {
          const isActive =
            allIds.some((id) => selectedLocationIds.has(id)) ||
            isManufacturerGroupActive;

          return isActive ? 1 : 0;
        }

        const isActive =
          allIds.some((id) => selectedLocationIds.has(id)) ||
          selectedCustomerIds.has(option.customer.id) ||
          isCustomerGroupActive;

        return isActive ? 1 : 0;
      }) +
      sumBy(extraOptions, (option) => (option.isActive ? 1 : 0)) +
      orphanCount,
    [
      allOptions,
      extraOptions,
      isCustomerGroupActive,
      isManufacturerGroupActive,
      orphanCount,
      selectedCustomerIds,
      selectedLocationIds,
    ]
  );

  const handleClearOrphans = () => {
    props.onChange(
      without(props.locationIds, ...orphanSelection.locationIds),
      without(props.customerIds, ...orphanSelection.customerIds),
      props.companyTypes
    );
  };

  // --- Manufacturer handlers ---

  /**
   * Handles selection changes for the manufacturer group.
   * Updates selection state for all manufacturer locations and their zones.
   */
  const handleChangeManufacturerGroup = (isActive: boolean) => {
    const allManufacturerIds = allManufacturerOptions.flatMap(getSiteAllIds);
    const locationIds = without(props.locationIds, ...allManufacturerIds);

    if (isActive) {
      return props.onChange(locationIds, props.customerIds, [
        ...props.companyTypes,
        'manufacturer',
      ]);
    }

    props.onChange(
      locationIds,
      props.customerIds,
      without(props.companyTypes, 'manufacturer')
    );
  };

  /**
   * Handles selection changes for individual manufacturer sites.
   * Adds/removes the site ID and all zone IDs, with promotion to
   * manufacturer group when all sites become selected.
   */
  const handleChangeManufacturerSite = (
    option: { id: string; zones: { id: string }[] },
    isActive: boolean
  ) => {
    const siteAllIds = getSiteAllIds(option);
    const allManufacturerIds = allManufacturerOptions.flatMap(getSiteAllIds);

    if (isActive) {
      const locationIds = uniq([...props.locationIds, ...siteAllIds]);
      const isAllSelected = allManufacturerIds.every((id) =>
        locationIds.includes(id)
      );

      if (isAllSelected) {
        return props.onChange(
          without(locationIds, ...allManufacturerIds),
          props.customerIds,
          [...props.companyTypes, 'manufacturer']
        );
      }

      return props.onChange(locationIds, props.customerIds, props.companyTypes);
    }

    // Deselecting: if manufacturer group was active, explode into
    // individual IDs for the remaining sites
    if (isManufacturerGroupActive) {
      const remainingIds = allManufacturerIds.filter(
        (id) => !siteAllIds.includes(id)
      );

      return props.onChange(
        [...props.locationIds, ...remainingIds],
        props.customerIds,
        without(props.companyTypes, 'manufacturer')
      );
    }

    props.onChange(
      without(props.locationIds, ...siteAllIds),
      props.customerIds,
      without(props.companyTypes, 'manufacturer')
    );
  };

  /**
   * Handles selection changes for individual manufacturer zones.
   * Adds/removes just the zone ID, with promotion when all manufacturer
   * sites become fully selected.
   */
  const handleChangeManufacturerZone = (
    zone: { id: string },
    isActive: boolean
  ) => {
    const allManufacturerIds = allManufacturerOptions.flatMap(getSiteAllIds);

    if (isActive) {
      const locationIds = [...props.locationIds, zone.id];
      const isAllSelected = allManufacturerIds.every((id) =>
        locationIds.includes(id)
      );

      if (isAllSelected) {
        return props.onChange(
          without(locationIds, ...allManufacturerIds),
          props.customerIds,
          [...props.companyTypes, 'manufacturer']
        );
      }

      return props.onChange(locationIds, props.customerIds, props.companyTypes);
    }

    // Deselecting: if manufacturer group was active, explode
    if (isManufacturerGroupActive) {
      const remainingIds = without(allManufacturerIds, zone.id);

      return props.onChange(
        [...props.locationIds, ...remainingIds],
        props.customerIds,
        without(props.companyTypes, 'manufacturer')
      );
    }

    props.onChange(
      without(props.locationIds, zone.id),
      props.customerIds,
      props.companyTypes
    );
  };

  // --- Customer handlers ---

  /**
   * Handles selection changes for the top-level customer group.
   * Updates selection state for all customer locations and their zones.
   */
  const handleChangeCustomerGroup = (isActive: boolean) => {
    const allCustomerIds = allCustomerOptions.flatMap(getSiteAllIds);

    if (isActive) {
      return props.onChange(
        without(props.locationIds, ...allCustomerIds),
        [],
        [...props.companyTypes, 'customer']
      );
    }

    props.onChange(
      without(props.locationIds, ...allCustomerIds),
      [],
      without(props.companyTypes, 'customer')
    );
  };

  /**
   * Handles selection changes for a single customer group header.
   */
  const handleChangeCustomerGroupFor = (
    customerId: string,
    isActive: boolean
  ) => {
    const allCustomerLocationIds =
      allCustomerGroups[customerId]?.flatMap(getSiteAllIds) ?? [];
    const locationIds = without(props.locationIds, ...allCustomerLocationIds);

    if (isActive) {
      const willCompleteAllCustomers = Object.keys(customers).every(
        (id) => props.customerIds.includes(id) || id === customerId
      );

      if (willCompleteAllCustomers) {
        return props.onChange(
          locationIds,
          [],
          [...props.companyTypes, 'customer']
        );
      }

      return props.onChange(
        locationIds,
        [...props.customerIds, customerId],
        props.companyTypes
      );
    }

    // Deselecting: if the whole customer group was active, explode it into the
    // customers that remain rather than clearing all of them
    if (props.companyTypes.includes('customer')) {
      return props.onChange(
        locationIds,
        without(Object.keys(allCustomerGroups), customerId),
        without(props.companyTypes, 'customer')
      );
    }

    props.onChange(
      locationIds,
      without(props.customerIds, customerId),
      without(props.companyTypes, 'customer')
    );
  };

  /**
   * Handles selection changes for a single customer site.
   */
  const handleChangeCustomerSiteFor = (
    customerId: string,
    option: { id: string; zones: { id: string }[] },
    isActive: boolean
  ) => {
    const allCustomerLocationIds =
      allCustomerGroups[customerId]?.flatMap(getSiteAllIds) ?? [];
    const isGroupActive =
      props.customerIds.includes(customerId) ||
      props.companyTypes.includes('customer');
    const siteAllIds = getSiteAllIds(option);

    if (isActive) {
      const newLocationIds = uniq([...props.locationIds, ...siteAllIds]);
      const isCustomerLocationGroupActive = allCustomerLocationIds.every((id) =>
        newLocationIds.includes(id)
      );

      if (isCustomerLocationGroupActive) {
        const cleanedLocationIds = without(
          newLocationIds,
          ...allCustomerLocationIds
        );
        const willCompleteAllCustomers = Object.keys(customers).every(
          (id) => props.customerIds.includes(id) || id === customerId
        );

        if (willCompleteAllCustomers) {
          return props.onChange(
            cleanedLocationIds,
            [],
            [...props.companyTypes, 'customer']
          );
        }

        return props.onChange(
          cleanedLocationIds,
          [...props.customerIds, customerId],
          props.companyTypes
        );
      }

      return props.onChange(
        newLocationIds,
        props.customerIds,
        props.companyTypes
      );
    }

    // Deselecting: if group was active, explode
    if (isGroupActive) {
      const remainingIds = allCustomerLocationIds.filter(
        (id) => !siteAllIds.includes(id)
      );
      const locationIds = [...props.locationIds, ...remainingIds];

      if (props.companyTypes.includes('customer')) {
        const customerIds = without(Object.keys(allCustomerGroups), customerId);

        return props.onChange(
          locationIds,
          customerIds,
          without(props.companyTypes, 'customer')
        );
      }

      return props.onChange(
        locationIds,
        without(props.customerIds, customerId),
        without(props.companyTypes, 'customer')
      );
    }

    props.onChange(
      without(props.locationIds, ...siteAllIds),
      without(props.customerIds, customerId),
      without(props.companyTypes, 'customer')
    );
  };

  /**
   * Handles selection changes for a single customer zone.
   */
  const handleChangeCustomerZoneFor = (
    customerId: string,
    zone: { id: string },
    isActive: boolean
  ) => {
    const allCustomerLocationIds =
      allCustomerGroups[customerId]?.flatMap(getSiteAllIds) ?? [];
    const isGroupActive =
      props.customerIds.includes(customerId) ||
      props.companyTypes.includes('customer');

    if (isActive) {
      const newLocationIds = [...props.locationIds, zone.id];
      const isCustomerLocationGroupActive = allCustomerLocationIds.every((id) =>
        newLocationIds.includes(id)
      );

      if (isCustomerLocationGroupActive) {
        const cleanedLocationIds = without(
          newLocationIds,
          ...allCustomerLocationIds
        );
        const willCompleteAllCustomers = Object.keys(customers).every(
          (id) => props.customerIds.includes(id) || id === customerId
        );

        if (willCompleteAllCustomers) {
          return props.onChange(
            cleanedLocationIds,
            [],
            [...props.companyTypes, 'customer']
          );
        }

        return props.onChange(
          cleanedLocationIds,
          [...props.customerIds, customerId],
          props.companyTypes
        );
      }

      return props.onChange(
        newLocationIds,
        props.customerIds,
        props.companyTypes
      );
    }

    // Deselecting: if group was active, explode
    if (isGroupActive) {
      const remainingIds = without(allCustomerLocationIds, zone.id);
      const locationIds = [...props.locationIds, ...remainingIds];

      if (props.companyTypes.includes('customer')) {
        const customerIds = without(Object.keys(allCustomerGroups), customerId);

        return props.onChange(
          locationIds,
          customerIds,
          without(props.companyTypes, 'customer')
        );
      }

      return props.onChange(
        locationIds,
        without(props.customerIds, customerId),
        without(props.companyTypes, 'customer')
      );
    }

    props.onChange(
      without(props.locationIds, zone.id),
      without(props.customerIds, customerId),
      without(props.companyTypes, 'customer')
    );
  };

  // Every handler above closes over the current props, and a row only ever
  // calls one from a click — so the rows reach them through a ref rather than
  // being rebuilt whenever one of them changes identity
  const handlers = {
    clearOrphans: handleClearOrphans,
    // Looked up live rather than carried on the row: the option list is held
    // at its last content, and an extra's handler closes over the selection —
    // so a frozen one reports against the selection as it was two clicks ago
    changeExtra: (id: string, isActive: boolean) => {
      props.extraOptions?.find((extra) => extra.id === id)?.onChange(isActive);
    },
    changeManufacturerGroup: handleChangeManufacturerGroup,
    changeManufacturerSite: handleChangeManufacturerSite,
    changeManufacturerZone: handleChangeManufacturerZone,
    changeCustomerGroup: handleChangeCustomerGroup,
    changeCustomerGroupFor: handleChangeCustomerGroupFor,
    changeCustomerSiteFor: handleChangeCustomerSiteFor,
    changeCustomerZoneFor: handleChangeCustomerZoneFor,
  };
  const handlersRef = useRef(handlers);

  useEffect(() => {
    handlersRef.current = handlers;
  });

  // Flatten the grouped tree into a single linear list of rows. Each row
  // carries its own selection state and handler so the virtualized renderer can
  // draw just the visible slice without walking the hierarchy.
  //
  // A site and its "Default" zone select the same location ID, so rows are
  // keyed on their position in the tree rather than on that value.
  const getRows = useCallback(
    (searchTerm: string): FilterMenuRow[] => {
      const lowerSearch = searchTerm.trim().toLowerCase();
      const visible = lowerSearch
        ? allOptions.flatMap((option) => {
            const siteNameMatches = option.label
              .toLowerCase()
              .includes(lowerSearch);
            // A site sits under its customer, or under the organization when it
            // has none. Matching that header reveals the whole group, the same
            // way matching a site reveals all of its zones.
            const groupNameMatches = (
              option.customer?.name ?? user.organization.name
            )
              .toLowerCase()
              .includes(lowerSearch);
            const matchingZones = option.zones.filter((zone) =>
              zone.name.toLowerCase().includes(lowerSearch)
            );
            const isDefaultZoneVisible =
              hasDefaultZone(option) &&
              (siteNameMatches ||
                groupNameMatches ||
                DEFAULT_ZONE_LABEL.toLowerCase().includes(lowerSearch));

            if (siteNameMatches || groupNameMatches) {
              return [
                { ...option, visibleZones: option.zones, isDefaultZoneVisible },
              ];
            }

            if (matchingZones.length > 0 || isDefaultZoneVisible) {
              return [
                {
                  ...option,
                  visibleZones: matchingZones,
                  isDefaultZoneVisible,
                },
              ];
            }

            return [];
          })
        : allOptions.map((option) => ({
            ...option,
            visibleZones: option.zones,
            isDefaultZoneVisible: hasDefaultZone(option),
          }));

      const [manufacturerOptions, customerOptions] = partition(
        visible,
        (option) => option.customer === null
      );
      const customerGroups = groupBy(
        customerOptions,
        (option) => option.customer?.id
      );

      const rows: FilterMenuRow[] = [];

      // A group header stands for every location beneath it, including the ones
      // a search is hiding — what it writes is a company type, not the ids on
      // screen. So it is offered as a checkbox only while it covers exactly
      // what is shown. An active one keeps its checkbox either way: the only
      // click left to it is a deselect, which cannot reach a hidden row.
      const buildGroupRow = (
        group: {
          key: string;
          label: string;
          level?: number;
          isActive: boolean;
          isIndeterminate?: boolean;
          onChange: (isActive: boolean) => void;
        },
        isNarrowedBySearch: boolean
      ): FilterMenuRow =>
        group.isActive || !isNarrowedBySearch
          ? { kind: 'option', isEmphasized: true, ...group }
          : {
              kind: 'header',
              key: group.key,
              label: group.label,
              level: group.level,
            };

      // An active group keeps its header even with no rows beneath it, so the
      // filter stays visible and can still be cleared. The header is checked in
      // that state, so the only reachable click is a deselection.
      const hasManufacturerRows =
        manufacturerOptions.length > 0 || isManufacturerGroupActive;
      const hasCustomerRows =
        Object.keys(customerGroups).length > 0 || isCustomerGroupActive;

      // Headers can outlive their rows, so the empty state keys off the rows
      const hasVisibleLocations =
        manufacturerOptions.length > 0 || customerOptions.length > 0;

      // Searched on their label like every other row: an extra that ignored the
      // term would sit above results it does not match. An active one stays, so
      // a search can never hide a selection the user still has to be able to clear.
      const visibleExtras = extraOptions.filter(
        (option) =>
          option.isActive || option.label.toLowerCase().includes(lowerSearch)
      );

      if (visibleExtras.length > 0) {
        visibleExtras.forEach((option) => {
          rows.push({
            kind: 'option',
            key: `extra:${option.id}`,
            label: option.label,
            isActive: option.isActive,
            onChange: (isActive) =>
              handlersRef.current.changeExtra(option.id, isActive),
          });
        });

        if (hasManufacturerRows || hasCustomerRows) {
          rows.push({ kind: 'divider', key: 'divider:extras' });
        }
      }

      if (hasManufacturerRows) {
        const isIndeterminate =
          !isManufacturerGroupActive &&
          allManufacturerOptions.some((option) =>
            getSiteAllIds(option).some((id) => selectedLocationIds.has(id))
          );

        rows.push(
          buildGroupRow(
            {
              key: 'manufacturer',
              label: user.organization.name,
              isActive: isManufacturerGroupActive,
              isIndeterminate,
              onChange: (isActive) =>
                handlersRef.current.changeManufacturerGroup(isActive),
            },
            manufacturerOptions.length !== allManufacturerOptions.length
          )
        );

        manufacturerOptions.forEach((option) => {
          const selection = getSiteSelection(
            option,
            selectedLocationIds,
            isManufacturerGroupActive
          );
          rows.push({
            kind: 'option',
            key: `site:${option.id}`,
            level: 1,
            label: option.label,
            chip: option.isInventory ? INVENTORY_CHIP_LABEL : undefined,
            isActive: selection.isActive,
            isIndeterminate: selection.isIndeterminate,
            onChange: (isActive) =>
              handlersRef.current.changeManufacturerSite(option, isActive),
          });

          if (option.isDefaultZoneVisible) {
            rows.push({
              kind: 'option',
              key: `site:${option.id}:default`,
              level: 2,
              label: DEFAULT_ZONE_LABEL,
              chip: option.isInventory ? INVENTORY_CHIP_LABEL : undefined,
              isActive:
                isManufacturerGroupActive || selectedLocationIds.has(option.id),
              onChange: (isActive) =>
                handlersRef.current.changeManufacturerZone(
                  { id: option.id },
                  isActive
                ),
            });
          }

          option.visibleZones.forEach((zone) => {
            rows.push({
              kind: 'option',
              key: `zone:${zone.id}`,
              level: 2,
              label: zone.name,
              chip: zone.isInventory ? INVENTORY_CHIP_LABEL : undefined,
              isActive:
                isManufacturerGroupActive || selectedLocationIds.has(zone.id),
              onChange: (isActive) =>
                handlersRef.current.changeManufacturerZone(zone, isActive),
            });
          });
        });
      }

      if (hasCustomerRows) {
        if (hasManufacturerRows) {
          rows.push({ kind: 'divider', key: 'divider:customers' });
        }

        rows.push(
          buildGroupRow(
            {
              key: 'customers',
              label: 'Customers',
              isActive: isCustomerGroupActive,
              isIndeterminate: isCustomerGroupIndeterminate,
              onChange: (isActive) =>
                handlersRef.current.changeCustomerGroup(isActive),
            },
            customerOptions.length !== allCustomerOptions.length
          )
        );
      }

      Object.keys(customerGroups).forEach((customerId) => {
        const customer = customers[customerId];

        const allCustomerLocationIds =
          allCustomerGroups[customerId]?.flatMap(getSiteAllIds) ?? [];
        const isGroupActive =
          selectedCustomerIds.has(customerId) || isCustomerGroupActive;
        const isGroupIndeterminate =
          !isGroupActive &&
          allCustomerLocationIds.some((id) => selectedLocationIds.has(id));

        rows.push(
          buildGroupRow(
            {
              key: `customer:${customerId}`,
              level: 1,
              label: customer.name,
              isActive: isGroupActive,
              isIndeterminate: isGroupIndeterminate,
              onChange: (isActive) =>
                handlersRef.current.changeCustomerGroupFor(
                  customerId,
                  isActive
                ),
            },
            customerGroups[customerId].length !==
              (allCustomerGroups[customerId]?.length ?? 0)
          )
        );

        customerGroups[customerId].forEach((option) => {
          const selection = getSiteSelection(
            option,
            selectedLocationIds,
            isGroupActive
          );
          rows.push({
            kind: 'option',
            key: `customer:${customerId}:site:${option.id}`,
            level: 2,
            label: option.label,
            chip: option.isInventory ? INVENTORY_CHIP_LABEL : undefined,
            isActive: selection.isActive,
            isIndeterminate: selection.isIndeterminate,
            onChange: (isActive) =>
              handlersRef.current.changeCustomerSiteFor(
                customerId,
                option,
                isActive
              ),
          });

          if (option.isDefaultZoneVisible) {
            rows.push({
              kind: 'option',
              key: `customer:${customerId}:site:${option.id}:default`,
              level: 3,
              label: DEFAULT_ZONE_LABEL,
              chip: option.isInventory ? INVENTORY_CHIP_LABEL : undefined,
              isActive: isGroupActive || selectedLocationIds.has(option.id),
              onChange: (isActive) =>
                handlersRef.current.changeCustomerZoneFor(
                  customerId,
                  { id: option.id },
                  isActive
                ),
            });
          }

          option.visibleZones.forEach((zone) => {
            rows.push({
              kind: 'option',
              key: `customer:${customerId}:zone:${zone.id}`,
              level: 3,
              label: zone.name,
              chip: zone.isInventory ? INVENTORY_CHIP_LABEL : undefined,
              isActive: isGroupActive || selectedLocationIds.has(zone.id),
              onChange: (isActive) =>
                handlersRef.current.changeCustomerZoneFor(
                  customerId,
                  zone,
                  isActive
                ),
            });
          });
        });
      });

      if (!hasVisibleLocations) {
        rows.push({
          kind: 'message',
          key: 'no-results',
          text: lowerSearch === '' ? NO_LOCATIONS_MESSAGE : NO_MATCHES_MESSAGE,
        });
      }

      // Not a search result, so it renders regardless of the search term
      if (orphanCount > 0) {
        if (rows.length > 0) {
          rows.push({ kind: 'divider', key: 'divider:orphan' });
        }

        rows.push({
          kind: 'option',
          key: 'orphan',
          label: pluralize(orphanCount, {
            singular: 'other selected location',
          }),
          isActive: true,
          onChange: () => handlersRef.current.clearOrphans(),
        });
      }

      return rows;
    },
    [
      allCustomerGroups,
      allCustomerOptions,
      allManufacturerOptions,
      allOptions,
      customers,
      extraOptions,
      isCustomerGroupActive,
      isCustomerGroupIndeterminate,
      isManufacturerGroupActive,
      orphanCount,
      selectedCustomerIds,
      selectedLocationIds,
      user.organization.name,
    ]
  );

  return (
    <VirtualizedNestedFilterMenu
      label={label}
      count={count}
      minWidth={MIN_MENU_WIDTH}
      searchPlaceholder="Search locations..."
      scrollIndicatorLabel="Scroll to bottom of location list"
      getRows={getRows}
    />
  );
}
